const MINIMUM_SPLIT_LENGTH = 12
const FALLBACK_FRAGMENT_LENGTH = 60
// Room for a running header or footer between a passage and the page break it crosses, in reduced characters.
const PAGE_EDGE_ALLOWANCE = 120
// Long enough that a line repeated on a neighbouring page is a running header or footer rather than a short phrase, in
// reduced characters and not counting digits.
const MINIMUM_RUNNING_LINE_LENGTH = 12

const DIGITS = /\p{N}/gu

// Up to three digits, so that a year alone on the last line of a passage is kept.
const PAGE_NUMBER_ON_LAST_LINE = /\s*\n\s*\d{1,3}\s*$/u
const WHITESPACE_RUN = /\s+/gu
const ANYTHING_BUT_LETTERS_AND_DIGITS = /[^\p{L}\p{N}]/gu

/**
 * The cited text as it is shown to a reader: runs of whitespace become a single space, and a page number alone on the
 * last line (the page footer the extraction ran into) is dropped. `"The board\n  approved it.\n12"` becomes
 * `"The board approved it."`, while `"…ended on 31 December\n2025"` keeps its year.
 */
export function cleanCitedText(citedText: string): string {
  return citedText.replace(PAGE_NUMBER_ON_LAST_LINE, '').replace(WHITESPACE_RUN, ' ').trim()
}

/**
 * Lower-case letters and digits only, so that the cited text and a page's text compare equal however each extraction
 * spaced, punctuated, hyphenated or accented them. `"Co-operation: ﬁnancé"` becomes `"cooperationfinance"`.
 *
 * NFKD splits an accented letter into the letter and its accent, and a ligature such as `ﬁ` into its letters, so
 * dropping everything but letters and digits keeps the `e` of `é` and loses only the accent.
 */
function reduceText(text: string): string {
  return text.normalize('NFKD').toLowerCase().replace(ANYTHING_BUT_LETTERS_AND_DIGITS, '')
}

/**
 * The pdf.js find pattern for a reduced fragment: its characters in order, with anything other than letters and digits
 * allowed between them.
 */
function toFindPattern(fragment: string): string {
  return Array.from(fragment).join('[^a-z0-9]*')
}

function splitAcrossPageBreak(passage: string, earlierPage: string, laterPage: string): string[] | null {
  const splitLengths = Array.from(
    { length: Math.max(passage.length - 2 * MINIMUM_SPLIT_LENGTH + 1, 0) },
    (_, index) => passage.length - MINIMUM_SPLIT_LENGTH - index
  )
  const splitLength =
    splitLengths.find((length) => {
      const remainderIndex = laterPage.indexOf(passage.slice(length))

      return (
        earlierPage.slice(-(length + PAGE_EDGE_ALLOWANCE)).includes(passage.slice(0, length)) && remainderIndex !== -1 && remainderIndex <= PAGE_EDGE_ALLOWANCE
      )
    }) ?? null

  return splitLength === null ? null : [passage.slice(0, splitLength), passage.slice(splitLength)]
}

/**
 * The reduced lines that also appear on another of the pages once digits are ignored: the running headers and footers.
 * `"Board pack 3/12"` and `"Board pack 4/12"` on consecutive pages are both running lines. Longest first, so that a line
 * containing a shorter one is cut out whole.
 */
function runningLines(pageTexts: ReadonlyMap<number, string>): string[] {
  const linesPerPage = [...pageTexts.values()].map((pageText) => [...new Set(pageText.split('\n').map(reduceText))])
  const withoutDigits = (line: string) => line.replace(DIGITS, '')
  const pageCountPerLine = linesPerPage
    .flatMap((lines) => [...new Set(lines.map(withoutDigits))])
    .reduce((counts, line) => counts.set(line, (counts.get(line) ?? 0) + 1), new Map<string, number>())

  return [...new Set(linesPerPage.flat())]
    .filter((line) => withoutDigits(line).length >= MINIMUM_RUNNING_LINE_LENGTH && (pageCountPerLine.get(withoutDigits(line)) ?? 0) > 1)
    .sort((a, b) => b.length - a.length)
}

/**
 * The passage with the running lines an extraction carried into it cut out, as the pieces either side of them. Pieces
 * shorter than the minimum are dropped once the passage has been cut.
 */
function withoutRunningLines(passage: string, lines: string[]): string[] {
  const pieces = lines.reduce((remaining, line) => remaining.flatMap((piece) => piece.split(line)), [passage])

  return pieces.length === 1 ? pieces : pieces.filter((piece) => piece.length >= MINIMUM_SPLIT_LENGTH)
}

function locatePassage(passage: string, pageNumber: number, reducedPageText: (pageNumber: number) => string): string[] {
  const page = reducedPageText(pageNumber)
  const previousPage = reducedPageText(pageNumber - 1)
  const nextPage = reducedPageText(pageNumber + 1)

  if (passage === '') {
    return []
  }

  if (page.includes(passage)) {
    return [passage]
  }

  const acrossBreak = splitAcrossPageBreak(passage, page, nextPage) ?? splitAcrossPageBreak(passage, previousPage, page)

  if (acrossBreak !== null) {
    return acrossBreak
  }

  if (previousPage.includes(passage) || nextPage.includes(passage)) {
    return [passage]
  }

  return [passage.slice(0, FALLBACK_FRAGMENT_LENGTH), passage.slice(-FALLBACK_FRAGMENT_LENGTH)].filter((fragment) => page.includes(fragment))
}

/**
 * Builds the pdf.js find regular expression that highlights where the cited passages sit on a page, or `null` when
 * none of them can be found there.
 *
 * `pageTexts` holds the text of the cited page and of the pages either side of it. A passage is matched whole where it
 * can be, split across the page break before or after the cited page where it runs over one, and otherwise by its
 * opening or closing characters alone. Running headers and footers the cited text picked up at a page break are left
 * out, so they are not highlighted where the page draws them.
 */
export function citedPassagePattern(citedTexts: string[], pageNumber: number, pageTexts: ReadonlyMap<number, string>): string | null {
  const reducedPageText = (page: number) => reduceText(pageTexts.get(page) ?? '')
  const lines = runningLines(pageTexts)
  const fragments = [
    ...new Set(
      citedTexts.flatMap((citedText) =>
        withoutRunningLines(reduceText(cleanCitedText(citedText)), lines).flatMap((passage) => locatePassage(passage, pageNumber, reducedPageText))
      )
    ),
  ]

  return fragments.length === 0 ? null : fragments.map((fragment) => `(?:${toFindPattern(fragment)})`).join('|')
}
